import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ToggleButton from '../ToggleButton';
import './GroupedButton.css';

function classNames(...parts) {
  return parts.filter(Boolean).join(' ');
}

const DEFAULT_PORTAL_Z = 1000022;

function getPortalMenuStyle(triggerRect, menuEl, align) {
  const GAP = 8;
  const PAD = 8;
  const MIN_WIDTH = 170;
  const menuWidth = menuEl?.offsetWidth || MIN_WIDTH;
  const menuHeight = menuEl?.offsetHeight || 160;

  let left = align === 'left' ? triggerRect.left : triggerRect.right - menuWidth;
  let top = triggerRect.bottom + GAP;

  if (left < PAD) left = PAD;
  if (left + menuWidth > window.innerWidth - PAD) {
    left = Math.max(PAD, window.innerWidth - PAD - menuWidth);
  }
  if (top + menuHeight > window.innerHeight - PAD) {
    top = Math.max(PAD, triggerRect.top - GAP - menuHeight);
  }

  return {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
    minWidth: `${MIN_WIDTH}px`,
    zIndex: DEFAULT_PORTAL_Z,
  };
}

/**
 * Grouped button with floating dropdown items.
 *
 * @param {object} props
 * @param {Array<{id?: string|number, text: React.ReactNode, icon?: React.ReactNode, onClick?: Function, disabled?: boolean, className?: string}>} props.items
 * @param {React.ReactNode} [props.triggerIcon]
 * @param {React.ReactNode} [props.triggerText='...']
 * @param {'left'|'right'} [props.align='right']
 * @param {boolean} [props.disabled=false]
 * @param {boolean} [props.closeOnItemClick=true]
 * @param {boolean} [props.menuPortal=false] — Render menu on document.body (avoids overflow clipping).
 * @param {string} [props.className]
 * @param {string} [props.menuClassName]
 * @param {string} [props.itemClassName]
 * @param {string} [props.ariaLabel='Open actions']
 * @param {string} [props.triggerTitle] — Native tooltip on the trigger button.
 * @param {(isOpen: boolean) => void} [props.onOpenChange]
 */
function GroupedButton({
  items = [],
  triggerIcon,
  triggerText = '...',
  align = 'right',
  disabled = false,
  closeOnItemClick = true,
  menuPortal = false,
  className,
  menuClassName,
  itemClassName,
  ariaLabel = 'Open actions',
  triggerTitle,
  onOpenChange,
}) {
  const containerRef = useRef(null);
  const menuPortalRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);

  const hasItems = useMemo(() => Array.isArray(items) && items.length > 0, [items]);

  const syncMenuPosition = useCallback(() => {
    const trigger = containerRef.current;
    if (!trigger || typeof window === 'undefined') return;
    const rect = trigger.getBoundingClientRect();
    setMenuStyle(getPortalMenuStyle(rect, menuPortalRef.current, align));
  }, [align]);

  useLayoutEffect(() => {
    if (!isOpen || !menuPortal) {
      setMenuStyle(null);
      return undefined;
    }
    syncMenuPosition();
    const raf = window.requestAnimationFrame(() => syncMenuPosition());
    window.addEventListener('resize', syncMenuPosition);
    window.addEventListener('scroll', syncMenuPosition, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', syncMenuPosition);
      window.removeEventListener('scroll', syncMenuPosition, true);
    };
  }, [isOpen, menuPortal, syncMenuPosition, items.length]);

  useEffect(() => {
    if (!isOpen) return undefined;

    function handleOutsideClick(event) {
      if (containerRef.current?.contains(event.target)) return;
      if (menuPortalRef.current?.contains(event.target)) return;
      setIsOpen(false);
    }

    function handleEscape(event) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick, { passive: true });
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  useEffect(() => {
    if (onOpenChange) {
      onOpenChange(isOpen);
    }
  }, [isOpen, onOpenChange]);

  const handleTriggerClick = () => {
    if (disabled || !hasItems) {
      return;
    }
    setIsOpen((prev) => !prev);
  };

  const handleItemClick = (item, event) => {
    if (item.disabled) {
      return;
    }
    if (typeof item.onClick === 'function') {
      item.onClick(event, item);
    }
    if (closeOnItemClick) {
      setIsOpen(false);
    }
  };

  const menuList = isOpen ? (
    <ul
      ref={menuPortal ? menuPortalRef : undefined}
      className={classNames(
        'gb__menu',
        align === 'left' ? 'gb__menu--left' : 'gb__menu--right',
        menuPortal && 'gb__menu--portaled',
        menuClassName
      )}
      style={menuPortal && menuStyle ? menuStyle : undefined}
      role="menu"
    >
      {items.map((item, index) => {
        if (item.type === 'separator') {
          return (
            <li
              key={item.id ?? `separator-${index}`}
              role="separator"
              className="gb__menu-separator"
              aria-hidden="true"
            />
          );
        }

        if (item.type === 'label') {
          return (
            <li
              key={item.id ?? `label-${index}`}
              role="presentation"
              className={classNames('gb__menu-item-wrap', 'gb__menu-item-wrap--label', item.className)}
            >
              <span className="gb__menu-label">{item.text}</span>
            </li>
          );
        }

        if (item.type === 'toggle') {
          const toggled = Boolean(item.checked);
          return (
            <li
              key={item.id ?? `toggle-${index}`}
              role="none"
              className={classNames('gb__menu-item-wrap', 'gb__menu-item-wrap--toggle', item.className)}
            >
              <div
                className={classNames('gb__menu-item', 'gb__menu-item--toggle-row', item.rowClassName)}
                role="group"
                aria-label={
                  item.ariaLabel ||
                  (typeof item.text === 'string'
                    ? `${item.text}, ${toggled ? 'on' : 'off'}`
                    : undefined)
                }
              >
                {item.icon ? <span className="gb__menu-item-icon">{item.icon}</span> : null}
                <span className="gb__menu-item-text gb__menu-item-text--toggle">{item.text}</span>
                <ToggleButton
                  checked={toggled}
                  disabled={Boolean(item.disabled)}
                  onToggle={item.onToggle}
                  className={classNames('gb__menu-toggle', item.toggleClassName)}
                />
              </div>
            </li>
          );
        }

        return (
          <li
            key={item.id ?? `${String(item.text)}-${index}`}
            role="none"
            className={classNames('gb__menu-item-wrap', item.className, item.selected && 'gb__menu-item-wrap--selected')}
          >
            <button
              type="button"
              role={item.selected !== undefined ? 'menuitemradio' : 'menuitem'}
              aria-checked={item.selected !== undefined ? Boolean(item.selected) : undefined}
              className={classNames('gb__menu-item', itemClassName, item.selected && 'gb__menu-item--selected')}
              disabled={Boolean(item.disabled)}
              onClick={(event) => handleItemClick(item, event)}
            >
              {item.icon ? <span className="gb__menu-item-icon">{item.icon}</span> : null}
              <span className="gb__menu-item-text">{item.text}</span>
            </button>
          </li>
        );
      })}
    </ul>
  ) : null;

  return (
    <div className={classNames('gb', className)} ref={containerRef}>
      <button
        type="button"
        className="gb__trigger"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        disabled={disabled || !hasItems}
        title={triggerTitle}
        onClick={handleTriggerClick}
      >
        {triggerIcon ? <span className="gb__trigger-icon">{triggerIcon}</span> : null}
        {triggerText ? <span className="gb__trigger-text">{triggerText}</span> : null}
      </button>

      {menuPortal && menuList && typeof document !== 'undefined'
        ? createPortal(menuList, document.body)
        : !menuPortal
          ? menuList
          : null}
    </div>
  );
}

export default GroupedButton;
